/**
 * Platform-aware feature matrix — UI shows only what each network supports.
 */

export type SocialPlatformId =
  | 'facebook'
  | 'instagram'
  | 'whatsapp'
  | 'linkedin'
  | 'x'
  | 'youtube'
  | 'google_business'
  | 'telegram'
  | 'threads'
  | 'pinterest'
  | 'tiktok';

export type PlatformTabId =
  | 'overview'
  | 'inbox'
  | 'messages'
  | 'posts'
  | 'stories'
  | 'reels'
  | 'comments'
  | 'reviews'
  | 'mentions'
  | 'replies'
  | 'videos'
  | 'templates'
  | 'campaigns'
  | 'contacts'
  | 'broadcasts'
  | 'analytics'
  | 'automation'
  | 'publishing';

export interface PlatformDefinition {
  id: SocialPlatformId;
  label: string;
  apiPlatform: string;
  brandColor: string;
  inbox: boolean;
  publishing: boolean;
  stories: boolean;
  reels: boolean;
  comments: boolean;
  reviews: boolean;
  mentions: boolean;
  dmInbox: boolean;
  templates: boolean;
  broadcasts: boolean;
  tabs: PlatformTabId[];
}

export const SOCIAL_PLATFORMS: PlatformDefinition[] = [
  {
    id: 'facebook',
    label: 'Facebook',
    apiPlatform: 'FACEBOOK',
    brandColor: '#1877F2',
    inbox: true,
    publishing: true,
    stories: false,
    reels: false,
    comments: true,
    reviews: true,
    mentions: true,
    dmInbox: true,
    templates: false,
    broadcasts: false,
    tabs: ['overview', 'inbox', 'posts', 'comments', 'reviews', 'analytics', 'automation'],
  },
  {
    id: 'instagram',
    label: 'Instagram',
    apiPlatform: 'INSTAGRAM',
    brandColor: '#E4405F',
    inbox: true,
    publishing: true,
    stories: true,
    reels: true,
    comments: true,
    reviews: false,
    mentions: true,
    dmInbox: true,
    templates: false,
    broadcasts: false,
    tabs: ['overview', 'messages', 'posts', 'stories', 'reels', 'comments', 'mentions', 'analytics'],
  },
  {
    id: 'whatsapp',
    label: 'WhatsApp',
    apiPlatform: 'WHATSAPP',
    brandColor: '#25D366',
    inbox: true,
    publishing: false,
    stories: false,
    reels: false,
    comments: false,
    reviews: false,
    mentions: false,
    dmInbox: true,
    templates: true,
    broadcasts: true,
    tabs: ['overview', 'inbox', 'templates', 'campaigns', 'contacts', 'broadcasts', 'analytics'],
  },
  {
    id: 'linkedin',
    label: 'LinkedIn',
    apiPlatform: 'LINKEDIN',
    brandColor: '#0A66C2',
    inbox: false,
    publishing: true,
    stories: false,
    reels: false,
    comments: true,
    reviews: false,
    mentions: false,
    dmInbox: false,
    templates: false,
    broadcasts: false,
    tabs: ['overview', 'posts', 'comments', 'analytics', 'publishing'],
  },
  {
    id: 'x',
    label: 'X',
    apiPlatform: 'TWITTER',
    brandColor: '#000000',
    inbox: false,
    publishing: true,
    stories: false,
    reels: false,
    comments: false,
    reviews: false,
    mentions: true,
    dmInbox: false,
    templates: false,
    broadcasts: false,
    tabs: ['overview', 'posts', 'replies', 'mentions', 'analytics', 'publishing'],
  },
  {
    id: 'youtube',
    label: 'YouTube',
    apiPlatform: 'YOUTUBE',
    brandColor: '#FF0000',
    inbox: false,
    publishing: true,
    stories: false,
    reels: false,
    comments: true,
    reviews: false,
    mentions: false,
    dmInbox: false,
    templates: false,
    broadcasts: false,
    tabs: ['overview', 'videos', 'comments', 'analytics', 'publishing'],
  },
  {
    id: 'google_business',
    label: 'Google Business',
    apiPlatform: 'GOOGLE_BUSINESS',
    brandColor: '#4285F4',
    inbox: false,
    publishing: false,
    stories: false,
    reels: false,
    comments: false,
    reviews: true,
    mentions: false,
    dmInbox: false,
    templates: false,
    broadcasts: false,
    tabs: ['overview', 'reviews', 'analytics'],
  },
  {
    id: 'telegram',
    label: 'Telegram',
    apiPlatform: 'TELEGRAM',
    brandColor: '#26A5E4',
    inbox: true,
    publishing: false,
    stories: false,
    reels: false,
    comments: false,
    reviews: false,
    mentions: false,
    dmInbox: true,
    templates: false,
    broadcasts: false,
    tabs: ['overview', 'inbox', 'analytics'],
  },
  {
    id: 'threads',
    label: 'Threads',
    apiPlatform: 'THREADS',
    brandColor: '#101010',
    inbox: false,
    publishing: true,
    stories: false,
    reels: false,
    comments: true,
    reviews: false,
    mentions: true,
    dmInbox: false,
    templates: false,
    broadcasts: false,
    tabs: ['overview', 'posts', 'comments', 'mentions', 'analytics', 'publishing'],
  },
  {
    id: 'pinterest',
    label: 'Pinterest',
    apiPlatform: 'PINTEREST',
    brandColor: '#E60023',
    inbox: false,
    publishing: true,
    stories: false,
    reels: false,
    comments: true,
    reviews: false,
    mentions: false,
    dmInbox: false,
    templates: false,
    broadcasts: false,
    tabs: ['overview', 'posts', 'comments', 'analytics', 'publishing'],
  },
  {
    id: 'tiktok',
    label: 'TikTok',
    apiPlatform: 'TIKTOK',
    brandColor: '#010101',
    inbox: false,
    publishing: true,
    stories: false,
    reels: true,
    comments: true,
    reviews: false,
    mentions: true,
    dmInbox: false,
    templates: false,
    broadcasts: false,
    tabs: ['overview', 'posts', 'reels', 'comments', 'analytics', 'publishing'],
  },
];

export function getPlatformById(id: string): PlatformDefinition | undefined {
  const normalized = id.toLowerCase().replace(/-/g, '_') as SocialPlatformId;
  return SOCIAL_PLATFORMS.find((p) => p.id === normalized);
}

export function getPlatformByApiCode(code: string): PlatformDefinition | undefined {
  const upper = code.trim().toUpperCase();
  return SOCIAL_PLATFORMS.find((p) => p.apiPlatform === upper || p.apiPlatform === upper.replace('_PROFILE', ''));
}

export function platformSupportsInbox(platform: PlatformDefinition): boolean {
  return platform.inbox && platform.dmInbox;
}

export const PLATFORM_TAB_LABELS: Record<PlatformTabId, string> = {
  overview: 'Overview',
  inbox: 'Inbox',
  messages: 'Messages',
  posts: 'Posts',
  stories: 'Stories',
  reels: 'Reels',
  comments: 'Comments',
  reviews: 'Reviews',
  mentions: 'Mentions',
  replies: 'Replies',
  videos: 'Videos',
  templates: 'Templates',
  campaigns: 'Campaigns',
  contacts: 'Contacts',
  broadcasts: 'Broadcasts',
  analytics: 'Analytics',
  automation: 'Automation',
  publishing: 'Publishing',
};

export const INBOX_PLATFORMS = SOCIAL_PLATFORMS.filter(platformSupportsInbox).map((p) => p.apiPlatform);

export const PUBLISHING_PLATFORMS = SOCIAL_PLATFORMS.filter((p) => p.publishing).map((p) => p.apiPlatform);
